import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import client from '../api/client';

const planColor = { FREE:'#4a6580', STARTER:'#1a5cff', BASIC:'#1a5cff', PRO:'#7c3aed', GROWTH:'#7c3aed', BUSINESS:'#059669', ENTERPRISE:'#d97706' };

const Kpi = ({ label, value, bg, fg }) => (
  <div style={{ background:bg, borderRadius:12, padding:'14px 16px', minWidth:0 }}>
    <div style={{ fontSize:10, letterSpacing:0.8, color:fg, opacity:0.85, textTransform:'uppercase' }}>{label}</div>
    <div style={{ fontSize:24, fontWeight:700, color:fg, marginTop:2 }}>{value ?? '—'}</div>
  </div>
);

const Tile = ({ n, icon, value, label, sub, onClick }) => (
  <div onClick={onClick} style={{ position:'relative', border:'1px solid #eee', borderRadius:12, padding:'14px 16px', cursor:'pointer', background:'#fff', minWidth:0 }}>
    <span style={{ position:'absolute', top:10, right:12, fontSize:11, color:'#bbb' }}>{n}</span>
    <div style={{ fontSize:18 }}>{icon}</div>
    {value !== undefined && <div style={{ fontSize:22, fontWeight:700, color:'#111', marginTop:6 }}>{value}</div>}
    <div style={{ fontSize:13, color:'#333', marginTop: value !== undefined ? 2 : 20 }}>{label}</div>
    {sub && <div style={{ fontSize:11, color:'#999', marginTop:2 }}>{sub}</div>}
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  useEffect(() => { client.get('/admin/stats').then(r=>setStats(r.data)).catch(()=>{}).finally(()=>setLoading(false)); }, []);

  const c = stats?.counts || {};
  const [sendPlans, setSendPlans] = useState([]);
  useEffect(() => { client.get('/admin/marketing/send-plans').then(r => setSendPlans(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);
  const revenue = stats?.revenueByPlan?.reduce((s,p)=>s+Number(p.revenue||0),0) || 0;

  const kpis = [
    ['Tenants', stats?.totalTenants, '#E6F1FB', '#0C447C'],
    ['Onboarded', c.onboardedTenants, '#E1F5EE', '#0F6E56'],
    ['Documents', stats?.totalDocuments, '#EEEDFE', '#3C3489'],
    ['Cancellations', c.cancellationsPending, '#FAEEDA', '#854F0B'],
    ['Est. MRR', '$'+revenue.toFixed(0), '#FBEAF0', '#72243E'],
  ];

  const tiles = [ /* TILES_MENU_ORDER_v1 - order mirrors TopBar NAV */
    ['\u{1F3E2}', stats?.totalTenants, 'Tenants', (stats?.activeTenants ?? 0)+' active', '/tenants'],
    ['\u{1F4E3}', c.campaigns, 'Marketing', (c.campaignsDraft ?? 0)+' draft', '/marketing'],
    ['\u2728', undefined, 'New campaign', 'wizard', '/marketing-wizard'],
    ['\u{1F3AF}', c.engagedLeads, 'Engagement leads', (c.campaigns ?? 0)+' campaigns', '/engagement-leads'],
    ['\u{1F5C4}\uFE0F', c.audienceGroups, 'Databases', Number(c.contacts ?? 0).toLocaleString()+' contacts', '/databases'],
    ['\u{1F4C5}', sendPlans.length, 'Send plans', sendPlans.filter(p=>p.status==='ACTIVE').length+' active', '/send-plans'],
    ['\u{1F6AB}', c.cancellations, 'Cancellation requests', (c.cancellationsPending ?? 0)+' pending', '/cancellation-requests'],
    ['\u{1F465}', c.staff, 'Staff', (c.staffActive ?? 0)+' active', '/staff'],
    ['\u{1F4CB}', c.plans, 'Plans', null, '/plans'],
    ['\u{1F511}', c.apiKeys, 'API keys', (c.apiKeysActive ?? 0)+' active', '/api-keys'],
    ['\u{1F4DD}', c.applications, 'Rep applications', (c.applicationsPending ?? 0)+' pending', '/applications'],
    ['\u{1F91D}', c.reps, 'Network partners', (c.repsActive ?? 0)+' active', '/network-partners'],
    ['\u{1F4B0}', c.commissions, 'Commissions', (c.commissionsPending ?? 0)+' pending', '/commissions'],
    ['\u2699\uFE0F', undefined, 'Commission settings', null, '/commission-settings'],
    ['\u{1F5FA}\uFE0F', c.regions, 'Regions and areas', (c.areas ?? 0)+' areas', '/regions-areas'],
    ['\u{1F4CA}', c.repActivity, 'Rep activity', (c.repActivityToday ?? 0)+' today', '/rep-activity'],
    ['\u{1F553}', c.auditLog, 'Audit log', (c.auditLogToday ?? 0)+' today', '/audit-log'],
  ];

  if (loading) return <div style={{ color:'#666', padding:20 }}>Loading...</div>;

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:10, marginBottom:22 }}>
        {kpis.map(([l,v,bg,fg]) => <Kpi key={l} label={l} value={v} bg={bg} fg={fg} />)}
      </div>

      <div style={{ fontSize:15, color:'#555', margin:'0 0 10px' }}>Sections</div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(165px,1fr))', gap:10, marginBottom:26 }}>
        {tiles.map((t,i) => <Tile key={t[2]} n={i+2} icon={t[0]} value={t[1]} label={t[2]} sub={t[3]} onClick={()=>navigate(t[4])} />)}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))', gap:14 }}>
        <div style={{ background:'#fff', borderRadius:12, padding:18, border:'1px solid #eee' }}>
          <div style={{ color:'#111', fontWeight:600, marginBottom:16, fontSize:14 }}>Tenants by plan</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={stats?.tenantsByPlan||[]} margin={{ left:-20 }}>
              <XAxis dataKey="plan" stroke="#888" tick={{ fontSize:11 }} />
              <YAxis stroke="#888" tick={{ fontSize:11 }} />
              <Tooltip contentStyle={{ background:'#fff', border:'1px solid #eee', borderRadius:8, color:'#111' }} />
              <Bar dataKey="count" radius={[4,4,0,0]}>
                {(stats?.tenantsByPlan||[]).map((e,i)=><Cell key={i} fill={planColor[e.plan]||'#1a5cff'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={{ background:'#fff', borderRadius:12, padding:18, border:'1px solid #eee', overflowX:'auto' }}>
          <div style={{ color:'#111', fontWeight:600, marginBottom:16, fontSize:14 }}>Revenue by plan</div>
          {stats?.revenueByPlan?.length ? (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>{['Plan','Tenants','Price','Revenue'].map(h=><th key={h} style={{ color:'#777', fontSize:12, textAlign:'left', padding:'0 8px 10px 0', fontWeight:500 }}>{h}</th>)}</tr></thead>
              <tbody>{stats.revenueByPlan.map((p,i)=>(
                <tr key={i}>
                  <td style={{ padding:'8px 0' }}><span style={{ background:(planColor[p.plan]||'#4a6580')+'22', color:planColor[p.plan]||'#444', padding:'2px 8px', borderRadius:4, fontSize:12, fontWeight:600 }}>{p.plan}</span></td>
                  <td style={{ color:'#333', fontSize:14, padding:'8px' }}>{p.tenants}</td>
                  <td style={{ color:'#333', fontSize:14, padding:'8px' }}>${p.price}</td>
                  <td style={{ color:'#0a8a40', fontSize:14, fontWeight:600, padding:'8px 0' }}>${Number(p.revenue).toFixed(0)}</td>
                </tr>
              ))}</tbody>
            </table>
          ) : <div style={{ color:'#777', fontSize:14 }}>No paid plans yet</div>}
        </div>
      </div>

      <div style={{ borderTop:'1px solid #eee', marginTop:24, paddingTop:14, display:'flex', justifyContent:'space-between', fontSize:13, flexWrap:'wrap', gap:8 }}>
        <span style={{ color:'#777' }}>System health</span>
        <span style={{ color:'#0a8a40' }}>OurEsign service active</span>
      </div>
    </div>
  );
}
